import type Phaser from "phaser";
import { COLLISION_CELLS_PER_TILE, propSolidAt, type ChunkProp, type FrameMask, type FrameMaskOf } from "daedalus-core";
import { NO_STAMPS, type CullArea, type EaseStamp } from "daedalus-renderer-phaser";
import { roofedAt } from "odyssey-shared";
import { featherAlpha } from "./feather.ts";

/**
 * Where the weather and the night are eased while the local character is sheltered by a roof (ADR-144's note of 2026-11-01, ADR-141
 * note 15). Who is sheltered is the one definition Drenched uses: `roofedAt` over the room plane of the painted roof areas, which the
 * map server can also know. The place on the screen that the shelter covers is the room's cells of that plane together with the
 * solid pixels of the roof prop's closed frame (the building seen from above, which the open roof no longer draws: the gable and the
 * walls that stand above the painted floor), the frame kept to the columns of the painted area widened to the walls so the roof's
 * eaves are left out. The plane decides who is sheltered and the frame only gives the place its height on the screen.
 */

/**
 * What the place needs to know of one driven roof.
 */
export interface RoofSpec {
  readonly id: string;
  readonly layerId: string;
  readonly prop: ChunkProp;
  /** The id of the frame the prop shows closed and its size in pixels; null when the appearance names no closed frame. */
  readonly footFrame: string | null;
  readonly width: number;
  readonly height: number;
  /** The columns of the world, `[left, right)` in pixels, the frame is kept to. */
  readonly left: number;
  readonly right: number;
}

/**
 * One roof's place and its state: the mask in the frame's own orientation, whether it holds the frame's pixels or only the plane's
 * cells so far, the art stamp that lays it, and when each is asked for again.
 */
interface Roof extends RoofSpec {
  mask: FrameMask | null;
  complete: boolean;
  outline: FrameMask | null;
  softKey: string | null;
  stamp: EaseStamp | null;
  softStamp: EaseStamp | null;
  maskRetryAt: number;
  stampRetryAt: number;
}

/**
 * The name of a canvas texture's only frame.
 */
const BASE_FRAME = "__BASE";

/**
 * How many pixels the soak's edge is feathered over at a building's outline.
 */
export const SOAK_FEATHER_PX = 3;

/**
 * How many updates pass before a frame that could not be read (its atlas page is not loaded yet) or a canvas that could not be
 * made is asked for again.
 */
export const RETRY_UPDATES = 30;

const NO_INDEXES: readonly number[] = [];

/**
 * The places of the roofs of a map.
 */
export class RoofPlace {
  private readonly roofs: Roof[];
  private readonly lit: Uint8Array;
  private readonly cellPixels: number;
  private plane: Uint8Array | undefined;
  private columns = 0;
  private rows = 0;
  private maskOf: FrameMaskOf | null = null;
  private updates = 0;
  private sheltered = false;
  private openIndexes: readonly number[] = NO_INDEXES;
  private openStamps: readonly EaseStamp[] = NO_STAMPS;
  private openSoft: readonly EaseStamp[] = NO_STAMPS;
  private readonly canvases: string[] = [];
  private builds = 0;
  private warnedRead = false;
  private warnedCanvas = false;

  /**
   * The places the local character is sheltered in, as a place a world pixel is or is not in: where the rain and the wind are not
   * drawn and the light is eased.
   */
  public readonly open: CullArea = { contains: (x, y) => this.coversOpen(x, y) };

  /**
   * @param scene The scene, for the canvas textures the stamps are drawn to.
   * @param tileSize Tile size in pixels.
   * @param specs The driven roofs, in the order of the room plane's ids: the room of the first is 1.
   */
  public constructor(private readonly scene: Phaser.Scene, private readonly tileSize: number, specs: readonly RoofSpec[]) {
    this.roofs = specs.map((spec) => ({ ...spec, mask: null, complete: false, outline: null, softKey: null, stamp: null, softStamp: null, maskRetryAt: 0, stampRetryAt: 0 }));
    this.lit = new Uint8Array(specs.length);
    this.cellPixels = tileSize / COLLISION_CELLS_PER_TILE;
  }

  /**
   * Gives the places the room plane that decides who is sheltered and what the painted floor covers.
   *
   * @param plane The plane, one byte an authoring cell, row-major; the room of the first roof is 1.
   * @param columns Its width in cells.
   * @param rows Its height in cells.
   */
  public useRooms(plane: Uint8Array, columns: number, rows: number): void {
    this.plane = plane;
    this.columns = columns;
    this.rows = rows;
    this.reset();
  }

  /**
   * Gives the places the solid pixels of the roofs' frames. A frame whose page cannot be read yet leaves the plane's cells alone as
   * the place and is asked for again every `RETRY_UPDATES` updates until it can be read.
   *
   * @param maskOf The atlas's masks, or null when they could not be read, which leaves the room's cells alone as the places.
   */
  public useMasks(maskOf: FrameMaskOf | null): void {
    this.maskOf = maskOf;
    this.reset();
  }

  /**
   * Forgets what was built from the plane and the masks.
   */
  private reset(): void {
    for (const roof of this.roofs) {
      roof.mask = null;
      roof.complete = false;
      roof.stamp = null;
      roof.softStamp = null;
      roof.outline = null;
      roof.softKey = null;
      roof.maskRetryAt = 0;
      roof.stampRetryAt = 0;
    }
    this.lit.fill(0);
    this.sheltered = false;
    this.openIndexes = NO_INDEXES;
    this.openStamps = NO_STAMPS;
    this.openSoft = NO_STAMPS;
  }

  /**
   * Works out who is sheltered and which roofs' places are open for the local character, and remakes the list and the stamps when
   * that changes: on entering or leaving a roof, never by a frame that changes nothing.
   *
   * @param inside Whether the character stands in the area of each roof (a byte each, in roof order).
   * @param x The character's feet x in sixteenths.
   * @param y The character's feet line in sixteenths.
   */
  public update(inside: Uint8Array, x: number, y: number): void {
    this.updates += 1;
    const sheltered = roofedAt(this.plane, this.columns, this.rows, x, y);
    let changed = sheltered !== this.sheltered;
    this.sheltered = sheltered;
    for (let i = 0; i < this.roofs.length; i += 1) {
      const on = sheltered && inside[i] === 1 ? 1 : 0;
      const roof = this.roofs[i] as Roof;
      if (on !== this.lit[i]) {
        changed = true;
        this.lit[i] = on;
      }
      if (on === 1 && (roof.stamp === null || !roof.complete)) {
        const had = roof.stamp;
        const wasComplete = roof.complete;
        if (this.stampOf(roof) !== had || roof.complete !== wasComplete) {
          changed = true;
        }
      }
    }
    if (changed) {
      this.rebuildOpen();
    }
  }

  /**
   * Whether the local character is sheltered by a roof, as of the last `update`: the same answer Drenched gives.
   *
   * @returns True under a roof.
   */
  public get isSheltered(): boolean {
    return this.sheltered;
  }

  /**
   * The art stamps of the places the local character is sheltered in, which the light layer eases the darkness by; the list is
   * replaced when the character is sheltered or not and is otherwise the same object.
   *
   * @returns The stamps, the shared empty list when none is open.
   */
  public get stamps(): readonly EaseStamp[] {
    return this.openStamps;
  }

  /**
   * The feathered outlines of the places the local character is sheltered in: the closed frame's whole silhouette (eaves and
   * gable, not cut to the walls' columns) with the room's cells, softened over `SOAK_FEATHER_PX`. The soak is cut by these, so the
   * ground stays dry right up to the building's edge. The list is replaced when the character is sheltered or not, and whenever a
   * roof's outline is built again (its frame page became readable), so a holder reseats on a change of the list.
   *
   * @returns The stamps, the shared empty list when none is open.
   */
  public get softStamps(): readonly EaseStamp[] {
    return this.openSoft;
  }

  /**
   * Whether a world pixel lies in the place of any roof, open or not: where a building stands whose roof can open, for what is never
   * to be laid under it (a puddle).
   *
   * @param x World x in pixels.
   * @param y World y in pixels.
   * @returns True when it does.
   */
  public coversAny(x: number, y: number): boolean {
    for (const roof of this.roofs) {
      if (this.solidAt(roof, x, y)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Whether every roof's place holds its frame's pixels, or there are no masks to read: when what is dropped by `coversAny` is final.
   *
   * @returns True when nothing more will be read.
   */
  public get ready(): boolean {
    for (const roof of this.roofs) {
      this.placeOf(roof);
      if (!roof.complete) {
        return false;
      }
    }
    return true;
  }

  /**
   * Removes the canvas textures the stamps were drawn to, when the page's stage ends.
   */
  public destroy(): void {
    for (const key of this.canvases) {
      this.scene.textures.remove(key);
    }
    this.canvases.length = 0;
  }

  /**
   * Remakes the open list and the stamps from the roofs lit now.
   */
  private rebuildOpen(): void {
    const indexes: number[] = [];
    const stamps: EaseStamp[] = [];
    const soft: EaseStamp[] = [];
    for (let i = 0; i < this.roofs.length; i += 1) {
      if (this.lit[i] === 1) {
        indexes.push(i);
        const roof = this.roofs[i] as Roof;
        if (roof.stamp !== null) {
          stamps.push(roof.stamp);
        }
        if (roof.softStamp !== null) {
          soft.push(roof.softStamp);
        }
      }
    }
    this.openSoft = soft.length === 0 ? NO_STAMPS : soft;
    this.openIndexes = indexes.length === 0 ? NO_INDEXES : indexes;
    this.openStamps = stamps.length === 0 ? NO_STAMPS : stamps;
  }

  /**
   * Whether a world pixel is in the place of one of the roofs the local character is sheltered by.
   *
   * @param x World x in pixels.
   * @param y World y in pixels.
   * @returns True when it is.
   */
  private coversOpen(x: number, y: number): boolean {
    const indexes = this.openIndexes;
    for (let i = 0; i < indexes.length; i += 1) {
      if (this.solidAt(this.roofs[indexes[i] as number] as Roof, x, y)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Whether a world pixel is in one roof's place.
   *
   * @param roof The roof.
   * @param x World x in pixels.
   * @param y World y in pixels.
   * @returns True when it is.
   */
  private solidAt(roof: Roof, x: number, y: number): boolean {
    const mask = this.placeOf(roof);
    return mask !== null && propSolidAt(roof.prop, mask, this.tileSize, x, y);
  }

  /**
   * One roof's place in the frame's own orientation: the room's cells of the plane, and the frame's solid pixels kept to the walls'
   * columns once the atlas page can be read. Built the first time it is asked for; while the page cannot be read the place is the
   * plane's cells alone and the frame is asked for again every `RETRY_UPDATES` updates.
   *
   * @param roof The roof.
   * @returns The place: the room's cells alone when there are no masks or no closed frame to read.
   */
  private placeOf(roof: Roof): FrameMask | null {
    if (roof.complete || this.updates < roof.maskRetryAt) {
      return roof.mask;
    }
    let read: FrameMask | null = null;
    const reads = this.maskOf !== null && roof.footFrame !== null;
    try {
      read = reads ? (this.maskOf as FrameMaskOf)(roof.footFrame as string) : null;
    } catch (error) {
      roof.maskRetryAt = this.updates + RETRY_UPDATES;
      if (!this.warnedRead) {
        this.warnedRead = true;
        console.warn(`the roof ${roof.id} frame could not be read, so its place on the screen is the painted floor until it can: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
    const solid = new Uint8Array(roof.width * roof.height);
    const outline = new Uint8Array(roof.width * roof.height);
    const roomId = this.roofs.indexOf(roof) + 1;
    const frameLeft = roof.prop.x * this.tileSize;
    const frameTop = (roof.prop.y + 1) * this.tileSize - roof.height;
    for (let row = 0; row < roof.height; row += 1) {
      const down = roof.prop.flipY ? roof.height - 1 - row : row;
      for (let column = 0; column < roof.width; column += 1) {
        const across = roof.prop.flipX ? roof.width - 1 - column : column;
        const worldX = frameLeft + across + 0.5;
        const worldY = frameTop + down + 0.5;
        const framed = read !== null && read.solid[row * read.w + column] === 1 && worldX >= roof.left && worldX < roof.right;
        const room = this.roomAt(worldX, worldY) === roomId;
        if (framed || room) {
          solid[row * roof.width + column] = 1;
        }
        if ((read !== null && read.solid[row * read.w + column] === 1) || room) {
          outline[row * roof.width + column] = 1;
        }
      }
    }
    roof.mask = { w: roof.width, h: roof.height, solid };
    roof.outline = { w: roof.width, h: roof.height, solid: outline };
    roof.complete = read !== null || !reads;
    roof.stamp = null;
    roof.softStamp = null;
    return roof.mask;
  }

  /**
   * The room of the plane at a world pixel.
   *
   * @param x World x in pixels.
   * @param y World y in pixels.
   * @returns The room's id, 0 where none.
   */
  private roomAt(x: number, y: number): number {
    const plane = this.plane;
    const column = Math.floor(x / this.cellPixels);
    const row = Math.floor(y / this.cellPixels);
    return plane === undefined || column < 0 || row < 0 || column >= this.columns || row >= this.rows ? 0 : (plane[row * this.columns + column] as number);
  }

  /**
   * The art stamp of one roof's place: a canvas texture of it in white, laid where the prop stands as the prop is, so the light layer
   * erases darkness on the place's own pixels. Made the first time the character is sheltered there, and again when the frame's
   * pixels join the plane's cells.
   *
   * @param roof The roof.
   * @returns The stamp, or null while the place cannot be read or no canvas can be made; a failing canvas is asked for again every
   *   `RETRY_UPDATES` updates.
   */
  private stampOf(roof: Roof): EaseStamp | null {
    const mask = this.placeOf(roof);
    if (mask === null) {
      return null;
    }
    if (roof.stamp !== null) {
      return roof.stamp;
    }
    if (this.updates < roof.stampRetryAt) {
      return null;
    }
    const textures = this.scene.textures;
    const key = `roof-place:${roof.layerId}:${roof.id}`;
    if (textures.exists(key)) {
      textures.remove(key);
    }
    const canvas = textures.createCanvas(key, mask.w, mask.h);
    if (canvas === null) {
      roof.stampRetryAt = this.updates + RETRY_UPDATES;
      if (!this.warnedCanvas) {
        this.warnedCanvas = true;
        console.warn(`a canvas for the roof ${roof.id} could not be made, so the light is not eased there until it can`);
      }
      return null;
    }
    const context = canvas.getContext();
    const pixels = context.createImageData(mask.w, mask.h);
    for (let at = 0; at < mask.solid.length; at += 1) {
      const to = at * 4;
      pixels.data[to] = 255;
      pixels.data[to + 1] = 255;
      pixels.data[to + 2] = 255;
      pixels.data[to + 3] = (mask.solid[at] as number) === 1 ? 255 : 0;
    }
    context.putImageData(pixels, 0, 0);
    canvas.refresh();
    if (!this.canvases.includes(key)) {
      this.canvases.push(key);
    }
    roof.stamp = { key, frame: BASE_FRAME, x: roof.prop.x * this.tileSize, y: (roof.prop.y + 1) * this.tileSize, flipX: roof.prop.flipX, flipY: roof.prop.flipY };
    roof.softStamp = this.softStampOf(roof, key);
    return roof.stamp;
  }

  /**
   * The feathered stamp of one roof's outline: a canvas texture of it in white, padded by the feather's width on every side.
   *
   * @param roof The roof, its outline built.
   * @param key The key of the roof's plain stamp, from which the soft one is named. Each build has a key of its own and a build removes the
   *   previous build's canvas and texture at once, so a rebuild keeps one canvas for the roof and never leaves one until `destroy`.
   *   The rebuild runs in `update`, which makes the new list the holder reseats from in the same frame's weather update, before
   *   anything is rendered.
   * @returns The stamp, or null when no canvas can be made.
   */
  private softStampOf(roof: Roof, key: string): EaseStamp | null {
    const outline = roof.outline as FrameMask;
    this.builds += 1;
    const softKey = `${key}:soft:${this.builds}`;
    const textures = this.scene.textures;
    if (roof.softKey !== null) {
      textures.remove(roof.softKey);
      this.canvases.splice(this.canvases.indexOf(roof.softKey), 1);
      roof.softKey = null;
    }
    const pad = SOAK_FEATHER_PX;
    const w = outline.w + 2 * pad;
    const h = outline.h + 2 * pad;
    const canvas = textures.createCanvas(softKey, w, h);
    if (canvas === null) {
      return null;
    }
    const alpha = featherAlpha(outline.solid, outline.w, outline.h, pad);
    const context = canvas.getContext();
    const pixels = context.createImageData(w, h);
    for (let at = 0; at < alpha.length; at += 1) {
      pixels.data[at * 4] = 255;
      pixels.data[at * 4 + 1] = 255;
      pixels.data[at * 4 + 2] = 255;
      pixels.data[at * 4 + 3] = alpha[at] as number;
    }
    context.putImageData(pixels, 0, 0);
    canvas.refresh();
    this.canvases.push(softKey);
    roof.softKey = softKey;
    return { key: softKey, frame: BASE_FRAME, x: roof.prop.x * this.tileSize - pad, y: (roof.prop.y + 1) * this.tileSize + pad, flipX: roof.prop.flipX, flipY: roof.prop.flipY };
  }
}
